import type { CampaignPauseReason, CampaignStatus, OfferEligibility, OfferKind } from "@townsy/contracts";
import { formatCoins } from "../lib/wallet";

/**
 * Plain-language copy for sponsored tasks (docs/07, ADR-0011). Honest by construction: a website task
 * is "an outbound click and return", never "verified reading"; coins are a virtual reward, never money;
 * nothing promises customers. The verification labels, the disclosure, the consent text and the
 * advertiser limitation come verbatim from `@townsy/contracts` (or from the server's DTOs).
 */

/** Daily sponsored-coin cap (docs/07 section 3 default); the server's `DailyRewardUsage.capCoins` wins when known. */
export const DEFAULT_DAILY_CAP_COINS = 100;

/**
 * An owner's earlier partner-confirmed ("sign-up") offer. ADR-0034 addendum (founder answers 2026-10-22): sign-up offers
 * are switched off until they come back (bootstrap `features.partnerOffersEnabled`; the server enforces it), so such an
 * offer is stopped: visitors don't see it and can't start it; the owner can remove it. With the switch on, it keeps
 * running as it is until removed (ADR-0034).
 */
export function earlierPartnerOfferNote(partnerOffersOn: boolean, where: "store_screen" | "campaign_card" | "campaign_editor"): string {
  if (partnerOffersOn) {
    if (where === "store_screen") return "An earlier kind of offer: it keeps running as it is until you remove it.";
    if (where === "campaign_card") return "An earlier kind of task: it keeps running as it is until you remove it; it can't be changed.";
    return "an earlier kind of task. It keeps running as it is until you remove it on the campaign card; it can't be changed.";
  }
  if (where === "store_screen") return "An earlier kind of offer, stopped for now: visitors don't see it or start it. You can remove it.";
  if (where === "campaign_card") return "An earlier kind of task, stopped for now: residents don't see it or start it. It can't be changed; you can remove it.";
  return "an earlier kind of task, stopped for now: residents don't see it or start it. It can't be changed; you can remove it on the campaign card.";
}

export const OFFER_KIND_LABEL: Record<OfferKind, string> = {
  website_visit: "Website visit",
  quiz: "Quiz",
  partner_callback: "Partner task",
};

/** One line on how each kind works, for residents (what Townsy can and cannot see). */
export function kindHowTo(kind: OfferKind, minReturnSeconds?: number | null): string {
  switch (kind) {
    case "website_visit":
      return `Open the sponsor's website from Townsy, come back after at least ${minReturnSeconds ?? 15} seconds, then claim. Townsy only records that you opened the link.`;
    case "quiz":
      return "Read a short explanation and answer one question here in Townsy. Townsy checks the answer on its server.";
    case "partner_callback":
      return "Complete the sponsor's step on their website. The reward becomes claimable only when the sponsor's server confirms it to Townsy.";
  }
}

export type EligibilityCta = "start" | "continue" | "sign_in" | "verify_email" | "show_active" | null;

export interface EligibilityCopy {
  /** The resident can press the primary button now. */
  canStart: boolean;
  /** Primary button text (when there is a button). */
  label: string;
  /** Why the task cannot be started (null when it can). */
  message: string | null;
  cta: EligibilityCta;
}

/**
 * What the offer row says and offers for each server eligibility. The server decides eligibility; this
 * only words it. `activeHere` = the viewer's own active attempt is on this offer.
 */
export function eligibilityCopy(e: OfferEligibility, ctx: { activeHere?: boolean; dailyCapCoins?: number | null } = {}): EligibilityCopy {
  const cap = ctx.dailyCapCoins ?? DEFAULT_DAILY_CAP_COINS;
  if (ctx.activeHere && e === "eligible") return { canStart: true, label: "Continue task", message: null, cta: "continue" };
  switch (e) {
    case "eligible":
      return { canStart: true, label: "Start task", message: null, cta: "start" };
    case "sign_in_required":
      return { canStart: false, label: "Create an account to earn coins", message: "Create an account to earn coins. Guests can look at tasks but cannot collect coins.", cta: "sign_in" };
    case "email_unverified":
      return { canStart: false, label: "Verify your email", message: "Verify your email address to earn coins (see your Account page).", cta: "verify_email" };
    case "already_earned":
      return { canStart: false, label: "Earned", message: "You already earned this reward. Each task pays once per account.", cta: null };
    case "quiz_failed":
      return { canStart: false, label: "Closed", message: "You used all tries of this quiz, so it is closed for you. Nothing was charged.", cta: null };
    case "daily_cap_reached":
      return { canStart: false, label: "Daily limit reached", message: `You reached today's limit of ${formatCoins(cap)} from sponsored tasks. Try again after midnight UTC.`, cta: null };
    case "daily_limit_reached":
      return { canStart: false, label: "Done for today", message: "This task reached its limit for today (UTC). Try again tomorrow.", cta: null };
    case "budget_exhausted":
      return { canStart: false, label: "No budget left", message: "The sponsor's budget for this task is used up right now.", cta: null };
    case "own_campaign":
      return { canStart: false, label: "Your campaign", message: "This is your own campaign: you can't earn coins from it.", cta: null };
    case "attempt_in_progress":
      return { canStart: false, label: "Show my task in progress", message: "You have another task in progress. Finish or cancel it first (one task at a time).", cta: "show_active" };
    case "too_close_to_midnight":
      return { canStart: false, label: "Try after midnight UTC", message: "Too little of today (UTC) is left to finish this task. Start it after midnight UTC.", cta: null };
    case "unavailable":
    default:
      return { canStart: false, label: "Not available", message: "This task is not available right now.", cta: null };
  }
}

export const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: "Draft",
  pending_review: "Waiting for review",
  approved: "Approved, not running",
  active: "Running",
  paused: "Paused",
  rejected: "Not approved",
};

export const CAMPAIGN_STATUS_TONE: Record<CampaignStatus, "info" | "success" | "warning" | "danger"> = {
  draft: "info",
  pending_review: "info",
  approved: "success",
  active: "success",
  paused: "warning",
  rejected: "danger",
};

export const PAUSE_REASON_LABEL: Record<CampaignPauseReason, string> = {
  owner: "Paused by you",
  // ADR-0029: most campaigns are free now; a paid one's budget meter still shows what stays yours.
  takeover: "Paused for good: the store changed owners.",
  staff: "Paused by Townsy staff. Edit the campaign and submit it for review again.",
};

export function campaignStatusText(status: CampaignStatus, pauseReason: CampaignPauseReason | null): string {
  if (status === "paused" && pauseReason) return PAUSE_REASON_LABEL[pauseReason];
  return CAMPAIGN_STATUS_LABEL[status];
}

/** "+10 coins" reward badge text. */
export function rewardText(coins: number): string {
  return `+${formatCoins(coins)}`;
}

/** Words that must never appear in sponsored-task copy (docs/07 section 1-2; checked by unit tests). */
export const FORBIDDEN_REWARD_PHRASES = ["verified reading", "earn money", "guaranteed customers", "verified interested buyer", "verified 15-second"] as const;
